/** @file request: one HTTP call to the Pose3D Worker; non-2xx replies and fetch failures throw. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { buildHeaders } from './buildHeaders';
import { buildUrl } from './buildUrl';
import type { ClientOptions, RequestInput, RequestResult } from './clientOptions';
import { toApiError } from './toApiError';
import type { FetchLike, RequestInitLike, ResponseLike } from './webTypes';

const HINT = 'check POSE3D_URL and connectivity';

function initOf(opts: ClientOptions, input: RequestInput): RequestInitLike {
  const headers = buildHeaders(opts, input);
  if (input.bytes !== undefined) return { method: input.method, headers, body: input.bytes };
  return input.body === undefined ? { method: input.method, headers } : { method: input.method, headers, body: JSON.stringify(input.body) };
}

async function send(opts: ClientOptions, url: string, init: RequestInitLike): Promise<ResponseLike> {
  const where = url.split('?')[0] ?? url;
  // A local binding: Workers reject `fetch` called as a method of another object ("Illegal invocation").
  const fetchFn: FetchLike | undefined = opts.fetch ?? (globalThis as { fetch?: FetchLike }).fetch;
  if (fetchFn === undefined) return throwError(ERROR_CODES.TRANSPORT, { url: where }, { hint: 'no global fetch: pass ClientOptions.fetch' });
  try {
    return await fetchFn(url, init);
  } catch {
    return throwError(ERROR_CODES.TRANSPORT, { url: where }, { hint: HINT });
  }
}

function parseJson(text: string, status: number): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return throwError(ERROR_CODES.TRANSPORT, { status }, { hint: 'the reply is not JSON; is the URL a Pose3D server?' });
  }
}

async function readValue(response: ResponseLike, accept: RequestInput['accept']): Promise<unknown> {
  if (accept === 'empty' || response.status === 204) return undefined;
  if (accept === 'bytes') return new Uint8Array(await response.arrayBuffer());
  const text = await response.text();
  return accept === 'text' ? text : parseJson(text, response.status);
}

async function readErrorBody(response: ResponseLike): Promise<unknown> {
  const text = await response.text().catch(() => '');
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/** Sends `input` with the client's credentials; the reply is decoded by `input.accept` and typed `T` by the caller. */
export async function request<T>(opts: ClientOptions, input: RequestInput): Promise<RequestResult<T>> {
  const response = await send(opts, buildUrl(opts, input), initOf(opts, input));
  if (!response.ok) throw toApiError(response.status, await readErrorBody(response));
  return { value: await readValue(response, input.accept) as T, headers: response.headers };
}
